import type { IssueSummary } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useIssueList } from '../../hooks/useIssueMgmt.js';
import { ISSUE_STATUS_LABEL_KEYS } from '../../lib/issue-mgmt-format.js';
import { IssueStatusBadge } from './IssueStatusBadge.js';

/**
 * 検索語を一覧に渡すまで待つ時間。
 * @graph-connects none
 */
const SEARCH_DELAY_MS = 300;

/**
 * 小窓に出す課題の数。
 * @graph-connects none
 */
const PICK_LIMIT = 20;

/**
 * 足すと判定待ちに戻る状態（保留・対応しない・解決済み）。
 * @graph-connects none
 */
const REOPENING_STATUSES: ReadonlySet<IssueSummary['displayStatus']> = new Set([
  'on_hold',
  'rejected',
  'resolved',
]);

/**
 * 「ほかの課題を探す」の小窓。番号・言葉で課題を探し（統合済みは出ない）、1つ選んで決定すると呼び出し元が足す。
 * 保留・対応しない・解決済みの課題には「足すと判定待ちに戻ります」と添える。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 振り分けで、おすすめに無い課題を番号や言葉で探して選ぶ小窓。足すと判定待ちに戻る課題には注意を添える
 * @graph-connects useIssueList [calls] 課題の検索
 * @graph-connects IssueStatusBadge [calls] 状態の札
 */
export function IssueSearchDialog({
  pointTitle,
  onClose,
  onPick,
  pending,
}: {
  pointTitle: string;
  onClose: () => void;
  onPick: (issue: IssueSummary) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query.trim(), SEARCH_DELAY_MS);
  const list = useIssueList(
    { query: debounced, themeId: null, includeHidden: false, unlinkedLimit: 1 },
    debounced !== '',
  );
  const [picked, setPicked] = useState<IssueSummary | null>(null);
  const issues = debounced === '' ? [] : (list.data?.issues ?? []).slice(0, PICK_LIMIT);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t('issueMgmt.searchIssue.title')}
      description={pointTitle}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">
            {picked === null ? t('issueMgmt.searchIssue.hint') : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {t('issueMgmt.common.cancel')}
            </Button>
            <Button
              disabled={picked === null}
              loading={pending}
              onClick={() => {
                if (picked !== null) onPick(picked);
              }}
            >
              {t('issueMgmt.searchIssue.add')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          aria-label={t('issueMgmt.searchIssue.search')}
          placeholder={t('issueMgmt.searchIssue.search')}
          leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          fullWidth
          autoFocus
        />
        {debounced !== '' && list.data !== undefined && issues.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('issueMgmt.searchIssue.empty')}
          </p>
        )}
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {issues.map((issue) => {
            const selected = picked?.id === issue.id;
            return (
              <button
                key={issue.id}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setPicked(issue);
                }}
                className={`flex w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                  selected
                    ? 'border-[var(--color-accent-primary)] bg-[color-mix(in_srgb,var(--color-accent-primary)_8%,transparent)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-border-hover)]'
                }`}
              >
                <span className="text-xs text-[var(--color-text-muted)]">{issue.id}</span>
                <span className="min-w-0 flex-1 font-medium text-[var(--color-text-primary)]">
                  {issue.title}
                </span>
                <IssueStatusBadge status={issue.displayStatus} />
                {REOPENING_STATUSES.has(issue.displayStatus) && (
                  <span className="w-full text-xs text-[var(--color-warning)]">
                    {t('issueMgmt.triage.reopenWarning', {
                      status: t(ISSUE_STATUS_LABEL_KEYS[issue.displayStatus]),
                    })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
